/**
 * "Open a case" in plain words (G-camp-cs-13, G-camp-fin-07, G-camp-ops-24): the type is picked
 * from the organisation's case types (Cases › Case types), and the information that type keeps
 * is asked one input per field, by its bilingual name, instead of one free-text type and one
 * "Fields" value whose keys the builder had to guess.
 */
import type { CaseType } from "../orgSettings";
import type { JsonSchemaLite } from "../types";
import { templateKey } from "./plain";

export const CASE_TOOL = "cases";
export const CASE_ACTION = "create_case";

/** Whether a system step opens a case. */
export function isCaseStep(tool: unknown, action: unknown): boolean {
  return tool === CASE_TOOL && action === CASE_ACTION;
}

/** The case type a step names as written (not a value from an earlier step). */
export function chosenCaseType(args: Record<string, unknown> | undefined): string | undefined {
  const v = args?.type;
  return typeof v === "string" && v.trim() && !templateKey(v) && !v.includes("{{") ? v.trim() : undefined;
}

/** The organisation's active case types, and the one the step names even when inactive. */
export function caseTypeChoices(types: readonly CaseType[], chosen?: string): CaseType[] {
  return types.filter((t) => t.active || t.key === chosen);
}

/** The schema of a case type's information: one field per value it keeps, by its plain name. */
export function caseFieldsSchema(type: CaseType): JsonSchemaLite {
  const properties: Record<string, JsonSchemaLite> = {};
  for (const f of type.fields) {
    properties[f.key] = {
      type: f.kind === "number" ? "number" : "string",
      // A bilingual title: the step panel shows it in the reader's language.
      title: { en: f.label.en || f.label.ar || f.key, ar: f.label.ar || f.label.en || f.key } as unknown as string,
      ...(f.kind === "date" ? { description: "YYYY-MM-DD" } : {}),
    };
  }
  const required = type.fields.filter((f) => f.required).map((f) => f.key);
  return { type: "object", properties, ...(required.length ? { required } : {}), additionalProperties: false };
}

/**
 * The "Open a case" form: `type` is one of the organisation's case types, and `fields` has one
 * input per value of the chosen type (required ones marked, so a step that misses one says so
 * before a run). Other actions' schemas are returned as they are.
 */
export function caseStepSchema(schema: JsonSchemaLite | undefined, types: readonly CaseType[], args: Record<string, unknown> | undefined): JsonSchemaLite | undefined {
  if (!schema?.properties || !types.length) return schema;
  const chosen = chosenCaseType(args);
  const choices = caseTypeChoices(types, chosen);
  const props: Record<string, JsonSchemaLite> = { ...schema.properties };
  if (props.type) props.type = { ...props.type, enum: choices.map((t) => t.key) };
  const type = chosen ? types.find((t) => t.key === chosen) : undefined;
  let required = [...(schema.required ?? [])];
  if (type && props.fields) {
    const fields = caseFieldsSchema(type);
    if (Object.keys(fields.properties ?? {}).length) props.fields = { ...fields, ...(props.fields.description ? { description: props.fields.description } : {}) };
    if (fields.required?.length && !required.includes("fields")) required = [...required, "fields"];
  }
  return { ...schema, properties: props, required };
}

/** A case type's plain name in the reader's language. */
export function caseTypeName(types: readonly CaseType[], key: string, lang: "ar" | "en"): string | undefined {
  const t = types.find((x) => x.key === key);
  if (!t) return undefined;
  return (lang === "ar" ? t.label.ar || t.label.en : t.label.en || t.label.ar) || undefined;
}

/** The step's `fields` value after one field changed: blank values are left out. */
export function withCaseField(fields: unknown, key: string, value: unknown): Record<string, unknown> | undefined {
  const next: Record<string, unknown> = fields && typeof fields === "object" && !Array.isArray(fields) ? { ...(fields as Record<string, unknown>) } : {};
  if (value === undefined || value === null || value === "") delete next[key];
  else next[key] = value;
  return Object.keys(next).length ? next : undefined;
}
